import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading } from "@/components/shared";
import { DatePicker } from "@/components/DatePicker";
import { EMPLOYMENT_CONTEXTS, WORKER_LOCATIONS, REGULATED_VALUES, RECOGNITION_STATUSES } from "@/lib/workerTypes";
import api, { formatApiErrorDetail } from "@/lib/api";

const WORK_AUTH = ["Authorized", "Restricted", "Not Authorized", "Pending"];
const EMP_TYPE = ["Full-time", "Part-time", "Fixed-term", "Apprenticeship", "Contract"];
const WORKER_CATEGORIES = ["GERMAN", "EU_EEA_SWISS", "NON_EU", "NOT_CLASSIFIED"];

function Field({ label, children, hint }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}{hint && <p className="text-xs text-slate-400">{hint}</p>}</div>;
}
function Section({ title, children }) {
  return <div className="card-soft p-6"><h3 className="mb-4 font-heading text-lg font-bold text-navy">{title}</h3><div className="grid gap-4 sm:grid-cols-2">{children}</div></div>;
}

const empty = {
  first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", nationality: "", worker_category: "", employee_number: "",
  job_title: "", department: "", employment_start_date: "", employment_end_date: "", employment_type: "",
  residence_status: "", permit_type: "", legal_basis: "", permit_valid_from: "", permit_valid_until: "",
  work_authorization_status: "", employer_restriction: false, arbeitgeberwechsel: false, notes: "",
  passport_number: "", passport_country: "", passport_valid_from: "", passport_valid_until: "",
  employment_context: "DIRECT_EMPLOYEE", worker_location_status: "UNKNOWN", place_of_birth: "",
  position_title: "", profession: "", regulated_profession: "UNKNOWN", recognition_status: "UNKNOWN",
  recognition_authority: "", recognition_date: "",
  foreign_employer: "", home_country: "", posting_start: "", posting_end: "", a1_valid_from: "", a1_valid_until: "", sending_institution: "",
  visa_type: "", visa_valid_until: "", issuing_authority: "",
};

export default function EmployeeForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (editing) api.get(`/employer/employees/${id}`).then((r) => {
      const d = r.data; const out = { ...empty };
      Object.keys(empty).forEach((k) => { out[k] = d[k] ?? empty[k]; });
      setF(out); setLoading(false);
    }).catch(() => { toast.error(t("tbl.emptyTitle")); nav("/app/employer/employees"); });
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    const payload = { ...f };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    payload.employer_restriction = Boolean(f.employer_restriction);
    payload.arbeitgeberwechsel = Boolean(f.arbeitgeberwechsel);
    if (!payload.email) delete payload.email;
    try {
      if (editing) { await api.put(`/employer/employees/${id}`, payload); toast.success(t("f.save")); nav(`/app/employer/employees/${id}`); }
      else { const { data } = await api.post("/employer/employees", payload); toast.success(t("f.create")); nav(`/app/employer/employees/${data.id}`); }
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <PageLoading />;

  return (
    <form onSubmit={submit} data-testid="employee-form">
      <Link to={editing ? `/app/employer/employees/${id}` : "/app/employer/employees"} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("f.back")}</Link>
      <h1 className="mb-6 font-heading text-2xl font-extrabold text-navy sm:text-3xl">{editing ? t("f.editTitle") : t("f.addTitle")}</h1>
      <div className="space-y-6">
        <Section title={t("f.personal")}>
          <Field label={t("f.firstName")}><Input required value={f.first_name} onChange={(e) => set("first_name")(e.target.value)} data-testid="emp-firstname" /></Field>
          <Field label={t("f.lastName")}><Input required value={f.last_name} onChange={(e) => set("last_name")(e.target.value)} data-testid="emp-lastname" /></Field>
          <Field label={t("f.email")} hint={t("f.emailHint")}><Input type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid="emp-email" /></Field>
          <Field label={t("f.phone")}><Input value={f.phone} onChange={(e) => set("phone")(e.target.value)} /></Field>
          <Field label={t("f.dob")}><DatePicker value={f.date_of_birth} onChange={set("date_of_birth")} testid="emp-dob" /></Field>
          <Field label={t("f.nationality")}><Input value={f.nationality} onChange={(e) => set("nationality")(e.target.value)} data-testid="emp-nationality" /></Field>
          <Field label={t("f.empNumber")}><Input value={f.employee_number} onChange={(e) => set("employee_number")(e.target.value)} /></Field>
        </Section>
        <Section title={t("f.employment")}>
          <Field label={t("f.jobTitle")}><Input value={f.job_title} onChange={(e) => set("job_title")(e.target.value)} data-testid="emp-jobtitle" /></Field>
          <Field label={t("f.department")}><Input value={f.department} onChange={(e) => set("department")(e.target.value)} /></Field>
          <Field label={t("f.startDate")}><DatePicker value={f.employment_start_date} onChange={set("employment_start_date")} /></Field>
          <Field label={t("f.endDate")}><DatePicker value={f.employment_end_date} onChange={set("employment_end_date")} /></Field>
          <Field label={t("f.empType")}>
            <Select value={f.employment_type || ""} onValueChange={set("employment_type")}><SelectTrigger><SelectValue placeholder={t("f.select")} /></SelectTrigger>
              <SelectContent>{EMP_TYPE.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
          </Field>
        </Section>
        <Section title={t("p2.s2")}>
          <Field label={t("f.workerCategory")}>
            <Select value={f.worker_category || ""} onValueChange={set("worker_category")}><SelectTrigger data-testid="edit-worker-category"><SelectValue placeholder={t("f.select")} /></SelectTrigger>
              <SelectContent>{WORKER_CATEGORIES.map((x) => <SelectItem key={x} value={x}>{t(`wc.${x}`)}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("p2.fContext")}>
            <Select value={f.employment_context || ""} onValueChange={set("employment_context")}><SelectTrigger data-testid="edit-context"><SelectValue placeholder={t("f.select")} /></SelectTrigger>
              <SelectContent>{EMPLOYMENT_CONTEXTS.map((x) => <SelectItem key={x} value={x}>{t(`p2.ctx.${x}`)}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("p2.fLocation")}>
            <Select value={f.worker_location_status || ""} onValueChange={set("worker_location_status")}><SelectTrigger data-testid="edit-location"><SelectValue placeholder={t("f.select")} /></SelectTrigger>
              <SelectContent>{WORKER_LOCATIONS.map((x) => <SelectItem key={x} value={x}>{t(`p2.loc.${x}`)}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("p2.positionTitle")}><Input value={f.position_title} onChange={(e) => set("position_title")(e.target.value)} data-testid="edit-position" /></Field>
          <Field label={t("p2.profession")}><Input value={f.profession} onChange={(e) => set("profession")(e.target.value)} /></Field>
          <Field label={t("p2.regulatedProfession")}>
            <Select value={f.regulated_profession || ""} onValueChange={set("regulated_profession")}><SelectTrigger data-testid="edit-regulated"><SelectValue placeholder={t("f.select")} /></SelectTrigger>
              <SelectContent>{REGULATED_VALUES.map((x) => <SelectItem key={x} value={x}>{t(`p2.reg.${x}`)}</SelectItem>)}</SelectContent></Select>
          </Field>
        </Section>
        {f.employment_context === "POSTED_WORKER" && (
          <Section title={t("p2.sec.POSTING")}>
            <Field label={t("p2.foreignEmployer")}><Input value={f.foreign_employer} onChange={(e) => set("foreign_employer")(e.target.value)} data-testid="edit-foreign-employer" /></Field>
            <Field label={t("p2.homeCountry")}><Input value={f.home_country} onChange={(e) => set("home_country")(e.target.value)} /></Field>
            <Field label={t("p2.postingStart")}><DatePicker value={f.posting_start} onChange={set("posting_start")} /></Field>
            <Field label={t("p2.postingEnd")}><DatePicker value={f.posting_end} onChange={set("posting_end")} /></Field>
            <Field label={t("p2.a1From")}><DatePicker value={f.a1_valid_from} onChange={set("a1_valid_from")} /></Field>
            <Field label={t("p2.a1Until")}><DatePicker value={f.a1_valid_until} onChange={set("a1_valid_until")} testid="edit-a1-until" /></Field>
            <Field label={t("p2.sendingInstitution")}><Input value={f.sending_institution} onChange={(e) => set("sending_institution")(e.target.value)} /></Field>
          </Section>
        )}
        {f.regulated_profession === "YES" && (
          <Section title={t("p2.sec.RECOGNITION")}>
            <Field label={t("p2.recognitionStatus")}>
              <Select value={f.recognition_status || ""} onValueChange={set("recognition_status")}><SelectTrigger data-testid="edit-recognition"><SelectValue placeholder={t("f.select")} /></SelectTrigger>
                <SelectContent>{RECOGNITION_STATUSES.map((x) => <SelectItem key={x} value={x}>{t(`p2.rec.${x}`)}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label={t("p2.recognitionAuthority")}><Input value={f.recognition_authority} onChange={(e) => set("recognition_authority")(e.target.value)} /></Field>
            <Field label={t("p2.recognitionDate")}><DatePicker value={f.recognition_date} onChange={set("recognition_date")} /></Field>
          </Section>
        )}
        <Section title={t("f.immigration")}>
          <Field label={t("f.residenceStatus")}><Input value={f.residence_status} onChange={(e) => set("residence_status")(e.target.value)} placeholder="§19c Abs. 1" data-testid="emp-residence" /></Field>
          <Field label={t("f.permitType")}><Input value={f.permit_type} onChange={(e) => set("permit_type")(e.target.value)} placeholder="Aufenthaltstitel" /></Field>
          <Field label={t("f.legalBasis")}><Input value={f.legal_basis} onChange={(e) => set("legal_basis")(e.target.value)} placeholder="§19c" /></Field>
          <Field label={t("f.workAuth")}>
            <Select value={f.work_authorization_status || ""} onValueChange={set("work_authorization_status")}><SelectTrigger data-testid="emp-workauth"><SelectValue placeholder={t("f.select")} /></SelectTrigger>
              <SelectContent>{WORK_AUTH.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("f.permitFrom")}><DatePicker value={f.permit_valid_from} onChange={set("permit_valid_from")} /></Field>
          <Field label={t("f.permitUntil")}><DatePicker value={f.permit_valid_until} onChange={set("permit_valid_until")} testid="emp-permit-until" /></Field>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2">
            <div><div className="text-sm font-medium text-navy">{t("f.restriction")}</div><div className="text-xs text-slate-400">{t("f.restrictionHint")}</div></div>
            <Switch checked={Boolean(f.employer_restriction)} onCheckedChange={set("employer_restriction")} data-testid="emp-restriction" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 sm:col-span-2">
            <div><div className="text-sm font-medium text-navy">{t("guide.awField")}</div><div className="text-xs text-slate-400">{t("guide.awFieldHint")}</div></div>
            <Switch checked={Boolean(f.arbeitgeberwechsel)} onCheckedChange={set("arbeitgeberwechsel")} data-testid="emp-arbeitgeberwechsel" />
          </div>
          <div className="sm:col-span-2"><Field label={t("f.notes")}><Textarea value={f.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} /></Field></div>
        </Section>
        <Section title={t("f.passport")}>
          <Field label={t("f.passportNumber")}><Input value={f.passport_number} onChange={(e) => set("passport_number")(e.target.value)} /></Field>
          <Field label={t("f.issuingCountry")}><Input value={f.passport_country} onChange={(e) => set("passport_country")(e.target.value)} /></Field>
          <Field label={t("f.validFrom")}><DatePicker value={f.passport_valid_from} onChange={set("passport_valid_from")} /></Field>
          <Field label={t("f.validUntil")}><DatePicker value={f.passport_valid_until} onChange={set("passport_valid_until")} testid="emp-passport-until" /></Field>
        </Section>
        {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>{t("f.cancel")}</Button>
          <Button type="submit" disabled={saving} className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="save-employee-btn">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("f.saving")}</> : editing ? t("f.save") : t("f.create")}
          </Button>
        </div>
      </div>
    </form>
  );
}
